import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Dialog } from "@/components/Dialog/Dialog";
import { ExportSettings } from "@/components/Export/ExportSettings";
import { InstallsExportOption } from "@/components/Export/InstallsExportOptions";
import { ContractExportOptions } from "@/components/Export/ContractExportOptions";
import { FAIExportOptions } from "@/components/Export/FAIExportOptions";
import { IntentExportOptions } from "@/components/Export/IntentExportOptions";
import { ProductsAndVendorsOptions } from "@/components/Export/SpendSheet/ProductsAndVendorsOptions";
import { CloudMaturityOptions } from "@/components/Export/SpendSheet/CloudMaturityOptions";
import { ITSpendOptions } from "@/components/Export/SpendSheet/ITSpendOptions";
import { useCustomColumns } from "@/components/Export/SpendSheet/hooks/useCustomColumns";
import { useCloudMaturity } from "@/components/Export/SpendSheet/hooks/useCloudMaturity";
import { BuyerIntentExportOptions } from "@/components/Export/BuyerIntentExportOptions";
import { MentionsExportOptions } from "@/components/Export/MentionsExportOptions";
import { GenAIExportOptions } from "@/components/Export/GenAIExportOptions";
import { AIScoresExportOptions } from "@/components/Export/AIScoresExportOptions";
import { RelativeSpendExportOptions } from "@/components/Export/RelativeSpendExportOptions";
import { RowBasedSpendExportOptions } from "@/components/Export/RowBasedSpendExportOptions";
import { AISpendExportOptions } from "@/components/Export/AISpendExportOptions";
import { ContactsExportOptions } from "@/components/Export/ContactsExportOptions";
import { ContactsFacExportOptions } from "@/components/Export/ContactsFacExportOptions";
import { FinancingEventsExportOptions } from "@/components/Export/FinancingEventsExportOptions";
import { MergersAcquisitionsExportOptions } from "@/components/Export/MergersAcquisitionsExportOptions";
import { UniverseJSONDialog } from "@/components/Export/UniverseJSONDialog";
import {
  defaultFirmographicsFields,
  defaultMentionTimeSeriesFields,
  defaultRowBasedSpendFirmographicsFields,
  defaultTimeSeriesFields,
} from "@/components/Export/exportConfigurationFields";
import {
  DEFAULT_FINANCING_EVENTS_FILTERS,
  pruneFinancingEventsFilters,
  type FinancingEventsFilters,
} from "@/components/Export/financingEvents";
import {
  DEFAULT_MERGERS_ACQUISITIONS_FILTERS,
  pruneMergersAcquisitionsFilters,
  type MergersAcquisitionsFilters,
} from "@/components/Export/mergersAcquisitions";
import { getHgOrgId, getLoggedUserProfile, getTenantId } from "@/utils/auth";
import {
  AI_SPEND_INCLUSION_TYPES,
  EXPORT_DATA_TYPES,
  EXPORT_FIELDS_INCLUSION_TYPES,
  EXPORT_INCLUSION_TYPES,
  type ExtraSearchFilters,
  type ExportFieldConfiguration,
} from "@/components/Export/types";
import { useSnackbar } from "@/components/Snackbar";
import {
  getExportResults,
  NIFFLER_ERROR_MESSAGES,
  NifflerErrorCode,
  NifflerExportError,
} from "@/apis/niffler/export";
import { getExportPayload } from "@/components/Export/utils";
import {
  parseSourceId,
  useScoringStore,
  v1SourceId,
  v2SourceId,
} from "@/store/scoringStore";
import type { ScoringProfile } from "@/lib/scoring/types";
import {
  sanitizeScoringCriteriaForBackend,
  transformFilterScoring,
} from "@/lib/scoring/scoringUtils";
import { formatNumber } from "@/utils/numberUtils";
import {
  InclusionMethod,
  type CompanyFilter as LegacyCompanyFilter,
  type InclusionFilter,
} from "@/legacy/types/companyFilter";
import { useFaiExportFilterParams } from "@/components/Export/hooks/useFaiExportFilterParams";
import { useOppGenFaiExportFilters } from "@/components/Export/hooks/useOppGenFaiExportFilters";
import { ExportErrorDialog } from "@/pages/Company/components/CompanyExport/ExportErrorDialog";
import { useHasCreditsEnforcement } from "@/hooks/useHasCreditsEnforcement";
import {
  estimateExportCredits,
  EstimateCreditsDetailResponse,
} from "@/apis/exportTrackingApi";
import { ExportCreditsAlert } from "@/components/Export/ExportCreditsAlert";
import { ExportQuoteDialog } from "@/components/ExportQuote/ExportQuoteDialog";
import { updateExportQuote } from "@/apis/bacchus/exportQuote";
import type {
  ExportQuote,
  ExportQuoteEstimate,
  ExportQuoteSearchFilters,
} from "@/apis/bacchus/types";
import { format } from "date-fns";
import { useFeatureFlag } from "@/hooks/useLaunchDarkly";
import type { IntentSignalScoreUi } from "@/components/Export/components/IntentThresholdSelector";
import type { MatchFileConfig } from "@/types/workspace";
import type { CachedTopic } from "@/types/intent";
import type { CompanyFilters } from "../types";
import { stripIntentUiFields } from "../utils/stripIntentUiFields";
import {
  buildContactFiltersForExport,
  contactFiltersNarrowCompanies,
} from "../utils/buildContactFilters";
import {
  getCompanyConfigInstallAge,
  getCompanyConfigSearchParams,
} from "../modules/companyConfig";
import {
  buildUniversePayload as buildUniverseExportPayload,
  type AIScoringSettings,
  type ContactsFacExportSettings,
} from "./universePayloadBuilder";
import {
  buildModelScoreFilter,
  withModelScoreFilterModels,
} from "../utils/buildModelScoreFilter";
import {
  ExportCategory,
  type ExportSettingItem,
  type ExportSettingsSnapshot,
} from "./types";
import { CreditsEstimateDialog } from "./CreditsEstimateDialog";
import { DIALOG_BODY_HEIGHT, MAX_EXPORT_COMPANIES } from "./constants";

const ENABLE_SALESFACING_QUOTING_TOOL_FF_KEY = "enableSalesfacingQuotingTool";
// launchdarkly-react-client-sdk camelCases flag keys (useCamelCaseFlagKeys
// defaults to true), so `universe-json-export` must be read as
// `universeJsonExport` — a kebab-case lookup is always undefined.
const UNIVERSE_JSON_EXPORT_FF_KEY = "universeJsonExport";
const AI_SCORING_DATA_FEED_FF_KEY = "aiScoringDataFeed";
// Matches OppGen's flag key exactly (FieldConfigurationCustomizerContent.tsx)
// so a single LaunchDarkly flag gates the mandatory-fields note + force-injection on both apps.
const MANDATORY_FIELDS_TRACKING_FF_KEY =
  "universeEnforceMandatoryFieldsForTracking";

export type CompanyExportModalProps = {
  open: boolean;
  onClose: () => void;
  /**
   * Export accepts the legacy `CompanyFilter` shape, but Segments (Plan C) uses
   * `OpportunityGeneratorFilter`. In practice, the shapes are compatible enough
   * for export as long as we strip nulls from arrays/objects.
   */
  filters: CompanyFilters | LegacyCompanyFilter | undefined;
  companyCount?: number;
  localizeInstalls?: boolean;
  whitespace?: MatchFileConfig;
  title?: string;
  /** The current sort in the grid, translated to backend format (e.g. "Fit Score Normalized-1"). */
  activeSortEntry?: { field: string; direction: string };
  /**
   * Saved settings (an export quote's) applied every time the modal opens,
   * over the defaults. Keys missing from an older snapshot keep the default.
   */
  initialSettings?: Partial<ExportSettingsSnapshot> | null;
  /** Shows Save Quote (with the quoting tool flag) — the new Segments UI only. */
  allowSaveQuote?: boolean;
  /** Active scoring sources, saved on the quote to restore the grid. */
  scoringSourceIds?: string[];
  /** The quote the modal was opened from: offers Update besides Save as New. */
  quote?: ExportQuote | null;
  /** Called after a quote is created or updated from the modal. */
  onQuoteSaved?: (quote: ExportQuote) => void;
};

type SnapshotStateKey = Exclude<
  keyof ExportSettingsSnapshot,
  "version" | "localizeInstalls" | "sort"
>;

type SnapshotSetters = {
  [K in SnapshotStateKey]: (value: ExportSettingsSnapshot[K]) => void;
};

// Choices the filter-derived defaults below can override one render after
// the snapshot's product ids land, so they're re-asserted on that render.
const FILTER_DERIVED_SNAPSHOT_KEYS = [
  "installInclusion",
  "faiInclusion",
  "intentInclusion",
  "intentSignalScore",
] as const satisfies readonly SnapshotStateKey[];

function toArray<T>(
  input: Array<T | null | undefined> | null | undefined
): T[] {
  if (!input) return [];
  return input.filter((v): v is T => v != null);
}

function stripNullsDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.filter((v) => v != null).map(stripNullsDeep);
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
      if (v === null || v === undefined) return;
      out[k] = stripNullsDeep(v);
    });
    return out;
  }
  return value;
}

/**
 * Company Search GraphQL spells the SIC keys `sicUs1987Ids`, but niffler/aeon
 * expect `sicUS1987Ids` and silently ignore unknown fields, so without this
 * rename a segment's SIC filter never reaches the export (CPF-2953).
 */
function toLegacyIndustryKeys(
  filters: Record<string, unknown>
): Record<string, unknown> {
  const industry = filters.industry as Record<string, unknown> | undefined;
  if (!industry) return filters;

  const {
    sicUs1987Ids,
    excludedSicUs1987Ids,
    ...rest
  }: Record<string, unknown> = industry;
  return {
    ...filters,
    industry: {
      ...rest,
      ...(sicUs1987Ids !== undefined && { sicUS1987Ids: sicUs1987Ids }),
      ...(excludedSicUs1987Ids !== undefined && {
        excludedSicUS1987Ids: excludedSicUs1987Ids,
      }),
    },
  };
}

function normalizeFiltersForExport(
  filters: CompanyExportModalProps["filters"]
): LegacyCompanyFilter | undefined {
  if (!filters) return undefined;
  const cleaned = stripNullsDeep(filters) as Record<string, unknown>;
  const stripped = stripIntentUiFields(cleaned) as Record<string, unknown>;
  return toLegacyIndustryKeys(stripped) as unknown as LegacyCompanyFilter;
}

const includedInclusionIds = (
  filters: LegacyCompanyFilter["products"] | LegacyCompanyFilter["vendors"]
) =>
  toArray(filters)
    .filter((filter) => filter.inclusionMethod !== InclusionMethod.NONE_PRESENT)
    .flatMap((filter) => toArray(filter.ids));

// OppGen's rule (CompanyListExportDialog, all-install-filters-for-export on):
// included products, vendors and categories count; product attributes and
// excluded entries don't. An empty result defaults Installs to NONE.
const checkIsInstallFilterEmpty = (filters: LegacyCompanyFilter | undefined) =>
  includedInclusionIds(filters?.products).length === 0 &&
  includedInclusionIds(filters?.vendors).length === 0 &&
  toArray(filters?.productCategories?.ids).length === 0;

const checkIsFaiFilterEmpty = (filters: LegacyCompanyFilter | undefined) => {
  const products = toArray(filters?.products).filter(
    (product) => product.inclusionMethod !== InclusionMethod.NONE_PRESENT
  );

  const flattenedProductIds = products.flatMap((product) =>
    toArray(product.ids)
  );
  const flattenedFaiIds = products.flatMap((product) => [
    ...toArray(product.departmentIds),
    ...toArray(product.roleIds),
  ]);

  return flattenedProductIds.length === 0 || flattenedFaiIds.length === 0;
};

const checkIsIntentFilterEmpty = (filters: LegacyCompanyFilter | undefined) => {
  const intentTopicIds = toArray(filters?.intent?.topics?.ids);
  return intentTopicIds.length === 0;
};

const checkIsBuyerIntentFilterEmpty = (
  filters: LegacyCompanyFilter | undefined
) => {
  const vendorIds = toArray(filters?.buyerIntentVendors).flatMap((f) =>
    toArray(f?.ids)
  );
  const productIds = toArray(filters?.buyerIntentProducts).flatMap((f) =>
    toArray(f?.ids)
  );
  const categoryIds = toArray(filters?.buyerIntentCategories?.ids);
  const attributeIds = toArray(filters?.buyerIntentAttributes?.ids);
  return (
    vendorIds.length +
      productIds.length +
      categoryIds.length +
      attributeIds.length ===
    0
  );
};

const defaultFieldConfig = (): ExportFieldConfiguration => ({
  inclusionType: EXPORT_FIELDS_INCLUSION_TYPES.ALL,
  fields: [],
});

// Matches OppGen: every sheet with a Firmographics sub-section defaults it to
// SELECTED with the curated field subset (all except relative_employees /
// relative_revenue), never "export all" — only the main field list defaults
// to ALL.
const defaultFieldConfigWithFirmographics = (): ExportFieldConfiguration => ({
  inclusionType: EXPORT_FIELDS_INCLUSION_TYPES.ALL,
  fields: [],
  firmographicsInclusionType: EXPORT_FIELDS_INCLUSION_TYPES.SELECTED,
  firmographicsFields: defaultFirmographicsFields,
});

// Matches OppGen: Time Series fields default to SELECTED with a curated
// subset, excluding Document Type Names.
const defaultTimeSeriesFieldConfig = (): ExportFieldConfiguration => ({
  inclusionType: EXPORT_FIELDS_INCLUSION_TYPES.SELECTED,
  fields: defaultTimeSeriesFields,
});

// Matches OppGen: Mention Time Series fields default to SELECTED too.
const defaultMentionTimeSeriesFieldConfig = (): ExportFieldConfiguration => ({
  inclusionType: EXPORT_FIELDS_INCLUSION_TYPES.SELECTED,
  fields: defaultMentionTimeSeriesFields,
});

// Matches OppGen: Row-Based Spend starts from its own firmographics subset.
const defaultRowBasedSpendFieldConfig = (): ExportFieldConfiguration => ({
  inclusionType: EXPORT_FIELDS_INCLUSION_TYPES.ALL,
  fields: [],
  firmographicsInclusionType: EXPORT_FIELDS_INCLUSION_TYPES.SELECTED,
  firmographicsFields: defaultRowBasedSpendFirmographicsFields,
});

const defaultContactsFacSettings = (): ContactsFacExportSettings => ({
  inclusion: EXPORT_INCLUSION_TYPES.NONE,
  productIds: [],
  configuration: defaultFieldConfigWithFirmographics(),
  contactsPerCompanyLimit: null,
});

function buildAIScoring(
  inclusion: EXPORT_INCLUSION_TYPES,
  sourceIds: string[],
  tiers: string[]
): AIScoringSettings {
  const tenantId = getTenantId() ?? undefined;
  const families =
    inclusion === EXPORT_INCLUSION_TYPES.SELECTED
      ? sourceIds.flatMap((sid) => {
          const parsed = parseSourceId(sid);
          return parsed?.kind === "v2" ? [parsed.family] : [];
        })
      : [];
  return tenantId && families.length > 0
    ? { tenantId, modelSelection: "selected", modelFamilies: families, tiers }
    : { tenantId, modelSelection: "none", modelFamilies: [], tiers: [] };
}

export const CompanyExportModal: React.FC<CompanyExportModalProps> = ({
  open,
  onClose,
  filters,
  companyCount = 0,
  localizeInstalls = true,
  whitespace,
  title = "Company List Export",
  activeSortEntry,
  initialSettings,
  allowSaveQuote = false,
  scoringSourceIds,
  quote,
  onQuoteSaved,
}) => {
  const hasCreditsEnforcement = useHasCreditsEnforcement();
  const isSalesfacingQuotingEnabled = useFeatureFlag(
    ENABLE_SALESFACING_QUOTING_TOOL_FF_KEY
  );
  const universeEnabled = useFeatureFlag(UNIVERSE_JSON_EXPORT_FF_KEY, false);
  // OppGen's flag for the FAI decision-makers option and FAI search filters.
  const enableNewFaiSheet = useFeatureFlag("enableNewFaiSheet", false);
  const aiScoringFlag = useFeatureFlag(AI_SCORING_DATA_FEED_FF_KEY, false);
  const aiScoresEnabled = universeEnabled && aiScoringFlag;
  const enforceMandatoryFields = useFeatureFlag(
    MANDATORY_FIELDS_TRACKING_FF_KEY,
    false
  );

  const [isCreditsDialogOpen, setIsCreditsDialogOpen] = useState(false);
  const [isUniverseJSONDialogOpen, setIsUniverseJSONDialogOpen] =
    useState(false);
  const { showSuccess, showError } = useSnackbar();

  const scoringV2Enabled = useFeatureFlag("maScoringModelsV2", false);
  const profiles = useScoringStore((s) => s.profiles);
  const activeProfileIds = useScoringStore((s) => s.activeProfileIds);
  const activeModelFamilies = useScoringStore((s) => s.activeModelFamilies);
  const scoringModels = useScoringStore((s) => s.models);
  const activeProfiles = useMemo(
    () =>
      activeProfileIds
        .map((id) => profiles.find((p) => p.id === id))
        .filter((p): p is ScoringProfile => p !== undefined),
    [profiles, activeProfileIds]
  );

  const activeModels = useMemo(
    () =>
      scoringV2Enabled
        ? activeModelFamilies
            .map((family) => scoringModels.find((m) => m.family === family))
            .filter((m): m is NonNullable<typeof m> => m !== undefined)
        : [],
    [scoringV2Enabled, scoringModels, activeModelFamilies]
  );

  // Displayed models plus any model a stored filter targets (RGI-2347): the
  // export must carry a filter's model in model_scores for it to apply, so it
  // counts/exports the same set the list shows.
  const exportModels = useMemo(
    () =>
      withModelScoreFilterModels(
        scoringV2Enabled,
        activeModels,
        scoringModels,
        filters as CompanyFilters | undefined
      ),
    [scoringV2Enabled, activeModels, scoringModels, filters]
  );

  const modelScoreFilter = useMemo(
    () =>
      buildModelScoreFilter(
        scoringV2Enabled,
        exportModels,
        filters as CompanyFilters | undefined
      ),
    [scoringV2Enabled, exportModels, filters]
  );

  const normalizedFilters = useMemo(
    () => normalizeFiltersForExport(filters),
    [filters]
  );

  const {
    getProductPresenceColumns,
    getVendorPresenceColumns,
    customColumns,
    addCustomColumn,
    updateCustomColumn,
    deleteCustomColumn,
    toggleColumnSelection,
    replaceColumns,
  } = useCustomColumns();

  const { isCloudMaturityEnabled, setIsCloudMaturityEnabled } =
    useCloudMaturity();

  const [spendInclusion, setSpendInclusion] = useState<EXPORT_INCLUSION_TYPES>(
    EXPORT_INCLUSION_TYPES.ALL
  );
  const [aiSpendSheetInclusion, setAiSpendSheetInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(EXPORT_INCLUSION_TYPES.NONE);

  const isInstallFilterEmpty = useMemo(
    () => checkIsInstallFilterEmpty(normalizedFilters),
    [normalizedFilters]
  );
  const isFaiFilterEmpty = useMemo(
    () => checkIsFaiFilterEmpty(normalizedFilters),
    [normalizedFilters]
  );
  const isIntentFilterEmpty = useMemo(
    () => checkIsIntentFilterEmpty(normalizedFilters),
    [normalizedFilters]
  );
  const isBuyerIntentFilterEmpty = useMemo(
    () => checkIsBuyerIntentFilterEmpty(normalizedFilters),
    [normalizedFilters]
  );

  const userProfile = getLoggedUserProfile();

  const [installProductIds, setInstallProductIds] = useState<number[]>([]);

  const initialInstallInclusion = useMemo(() => {
    if (installProductIds.length > 0) return EXPORT_INCLUSION_TYPES.SELECTED;
    if (isInstallFilterEmpty) return EXPORT_INCLUSION_TYPES.NONE;
    return EXPORT_INCLUSION_TYPES.FILTERS;
  }, [installProductIds.length, isInstallFilterEmpty]);

  const [installInclusion, setInstallInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(initialInstallInclusion);

  const [faiProductIds, setFaiProductIds] = useState<number[]>([]);
  const [faiDepartmentIds, setFaiDepartmentIds] = useState<string[]>([]);
  const [faiRoleIds, setFaiRoleIds] = useState<string[]>([]);
  const setFaiDepartmentAndRoleIds = useCallback(
    (departmentIds: string[], roleIds: string[]) => {
      setFaiDepartmentIds(departmentIds);
      setFaiRoleIds(roleIds);
    },
    []
  );

  const initialFaiInclusion = useMemo(() => {
    if (faiProductIds.length > 0) return EXPORT_INCLUSION_TYPES.SELECTED;
    return EXPORT_INCLUSION_TYPES.NONE;
  }, [faiProductIds.length]);

  const [faiInclusion, setFaiInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(initialFaiInclusion);

  // A quote's saved inclusions are restored as they were priced, so the
  // defaults derived from the filters only apply to a fresh export. Picking
  // install products still switches to SELECTED either way.
  const isRestoringQuote = !!initialSettings;
  const hasInstallProducts = installProductIds.length > 0;

  useEffect(() => {
    setInstallInclusion(initialInstallInclusion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInstallProducts]);

  useEffect(() => {
    if (isRestoringQuote) return;
    setInstallInclusion(initialInstallInclusion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInstallFilterEmpty]);

  useEffect(() => {
    setFaiInclusion(initialFaiInclusion);
  }, [initialFaiInclusion]);

  const [contractsInclusion, setContractsInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(EXPORT_INCLUSION_TYPES.NONE);

  const [intentInclusion, setIntentInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(EXPORT_INCLUSION_TYPES.NONE);
  const [intentTopicIds, setIntentTopicIds] = useState<string[]>([]);
  const [intentTopicsCache, setIntentTopicsCache] = useState<CachedTopic[]>([]);
  const [intentSignalScore, setIntentSignalScore] =
    useState<IntentSignalScoreUi>("ALL");

  useEffect(() => {
    if (!open || isRestoringQuote) return;
    setIntentInclusion(
      isIntentFilterEmpty
        ? EXPORT_INCLUSION_TYPES.NONE
        : EXPORT_INCLUSION_TYPES.FILTERS
    );
    // Always ALL, as before the Universe port: OppGen's MEDIUM/HIGH derivation
    // only applies to its non-universe flow, and with `universe-json-export`
    // off this modal must keep today's behaviour.
    setIntentSignalScore("ALL");
  }, [isIntentFilterEmpty, open, isRestoringQuote]);

  useEffect(() => {
    if (!open || isRestoringQuote) return;
    if (!isIntentFilterEmpty) return;
    if (intentInclusion !== EXPORT_INCLUSION_TYPES.FILTERS) return;
    setIntentInclusion(EXPORT_INCLUSION_TYPES.NONE);
  }, [intentInclusion, isIntentFilterEmpty, open, isRestoringQuote]);

  const [
    includeDecisionMakersAndInfluencersOnly,
    setIncludeDecisionMakersAndInfluencersOnly,
  ] = useState(false);

  const faiExportFilterParams = useFaiExportFilterParams(
    faiInclusion,
    (toArray(normalizedFilters?.products) as unknown as InclusionFilter[]) ||
      [],
    includeDecisionMakersAndInfluencersOnly
  );
  const universeFaiExportFilterParams = useOppGenFaiExportFilters(
    faiInclusion,
    normalizedFilters,
    includeDecisionMakersAndInfluencersOnly
  );

  // ── Universe export state ────────────────────────────────────────────────
  const [buyerIntentInclusion, setBuyerIntentInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(EXPORT_INCLUSION_TYPES.NONE);

  const [mentionInclusion, setMentionInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(EXPORT_INCLUSION_TYPES.NONE);
  const [mentionIds, setMentionIds] = useState<number[]>([]);
  const [mentionLookbackMonths, setMentionLookbackMonths] = useState<
    number | string
  >(24);
  const [mentionIncludeTimeSeries, setMentionIncludeTimeSeries] =
    useState(false);
  const [mentionFieldConfiguration, setMentionFieldConfiguration] =
    useState<ExportFieldConfiguration>(defaultFieldConfigWithFirmographics);

  const [generativeAIInclusion, setGenerativeAIInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(EXPORT_INCLUSION_TYPES.NONE);
  const [generativeAIFieldConfiguration, setGenerativeAIFieldConfiguration] =
    useState<ExportFieldConfiguration>(defaultFieldConfigWithFirmographics);

  const [relativeSpendInclusion, setRelativeSpendInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(EXPORT_INCLUSION_TYPES.NONE);
  const [relativeSpendIncludeSMB, setRelativeSpendIncludeSMB] = useState(false);
  const [relativeSpendFieldConfiguration, setRelativeSpendFieldConfiguration] =
    useState<ExportFieldConfiguration>(defaultFieldConfigWithFirmographics);

  const [rowBasedSpendInclusion, setRowBasedSpendInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(EXPORT_INCLUSION_TYPES.NONE);
  const [rowBasedSpendFieldConfiguration, setRowBasedSpendFieldConfiguration] =
    useState<ExportFieldConfiguration>(defaultRowBasedSpendFieldConfig);
  const [rowBasedSpendIncludeSMB, setRowBasedSpendIncludeSMB] = useState(false);

  const [aiSpendInclusion, setAISpendInclusion] =
    useState<AI_SPEND_INCLUSION_TYPES>(AI_SPEND_INCLUSION_TYPES.NONE);
  const [aiSpendIncludeSMB, setAISpendIncludeSMB] = useState(false);
  const [
    aiSpendAbsoluteFieldConfiguration,
    setAISpendAbsoluteFieldConfiguration,
  ] = useState<ExportFieldConfiguration>(defaultFieldConfigWithFirmographics);
  const [
    aiSpendRelativeFieldConfiguration,
    setAISpendRelativeFieldConfiguration,
  ] = useState<ExportFieldConfiguration>(defaultFieldConfigWithFirmographics);

  // ── NEW universe state additions ─────────────────────────────────────────
  // Installs universe extras
  const [installLookbackMonths, setInstallLookbackMonths] = useState<
    number | string
  >(24);
  const [installIncludeTimeSeries, setInstallIncludeTimeSeries] =
    useState(false);
  const [installFieldConfiguration, setInstallFieldConfiguration] =
    useState<ExportFieldConfiguration>(defaultFieldConfigWithFirmographics);
  const [timeSeriesLookbackMonths, setTimeSeriesLookbackMonths] = useState<
    number | string
  >("ALLTIME");
  const [timeSeriesFieldConfiguration, setTimeSeriesFieldConfiguration] =
    useState<ExportFieldConfiguration>(defaultTimeSeriesFieldConfig);

  // Intent universe extras
  const [intentCollectionIds, setIntentCollectionIds] = useState<string[]>([]);
  const [intentFieldConfiguration, setIntentFieldConfiguration] =
    useState<ExportFieldConfiguration>(defaultFieldConfigWithFirmographics);

  // BuyerIntent product ids
  const [buyerIntentProductIds, setBuyerIntentProductIds] = useState<number[]>(
    []
  );

  // Contract field config
  const [contractFieldConfiguration, setContractFieldConfiguration] =
    useState<ExportFieldConfiguration>(defaultFieldConfigWithFirmographics);

  // Spend extras
  const [spendFieldConfiguration, setSpendFieldConfiguration] =
    useState<ExportFieldConfiguration>(defaultFieldConfigWithFirmographics);
  const [spendIncludeSMB, setSpendIncludeSMB] = useState(false);

  // FAI field config
  const [faiFieldConfiguration, setFaiFieldConfiguration] =
    useState<ExportFieldConfiguration>(defaultFieldConfig);

  // Mention time series field config
  const [
    mentionTimeSeriesFieldConfiguration,
    setMentionTimeSeriesFieldConfiguration,
  ] = useState<ExportFieldConfiguration>(defaultMentionTimeSeriesFieldConfig);

  // ── Contacts export state ────────────────────────────────────────────────
  const [contactsInclusion, setContactsInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(EXPORT_INCLUSION_TYPES.NONE);
  const [contactsFieldConfiguration, setContactsFieldConfiguration] =
    useState<ExportFieldConfiguration>(defaultFieldConfigWithFirmographics);
  const [contactsPerCompanyLimit, setContactsPerCompanyLimit] = useState<
    number | null
  >(null);
  const [contactsFac, setContactsFac] = useState<ContactsFacExportSettings>(
    defaultContactsFacSettings
  );

  // Contacts and Contacts + Tech Installs are mutually exclusive, as in OppGen.
  const handleSetContactsInclusion = useCallback(
    (inclusion: EXPORT_INCLUSION_TYPES) => {
      setContactsInclusion(inclusion);
      if (inclusion !== EXPORT_INCLUSION_TYPES.NONE) {
        setContactsFac((current) => ({
          ...current,
          inclusion: EXPORT_INCLUSION_TYPES.NONE,
          productIds: [],
        }));
      }
    },
    []
  );
  const handleSetContactsFac = useCallback(
    (settings: ContactsFacExportSettings) => {
      setContactsFac(settings);
      if (settings.inclusion !== EXPORT_INCLUSION_TYPES.NONE) {
        setContactsInclusion(EXPORT_INCLUSION_TYPES.NONE);
      }
    },
    []
  );

  // ── Financing Events (Universe only) ─────────────────────────────────────
  const [financingEventsInclusion, setFinancingEventsInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(EXPORT_INCLUSION_TYPES.NONE);
  const [
    financingEventsFieldConfiguration,
    setFinancingEventsFieldConfiguration,
  ] = useState<ExportFieldConfiguration>(defaultFieldConfigWithFirmographics);
  const [aiScoresInclusion, setAIScoresInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(EXPORT_INCLUSION_TYPES.NONE);
  const [aiScoresSourceIds, setAIScoresSourceIds] = useState<string[]>([]);
  const [aiScoresTiers, setAIScoresTiers] = useState<string[]>([]);
  const [financingEventsFilters, setFinancingEventsFilters] =
    useState<FinancingEventsFilters>(DEFAULT_FINANCING_EVENTS_FILTERS);

  // ── M&A Events (Universe only) ───────────────────────────────────────────
  const [mergersAcquisitionsInclusion, setMergersAcquisitionsInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(EXPORT_INCLUSION_TYPES.NONE);
  const [
    mergersAcquisitionsFieldConfiguration,
    setMergersAcquisitionsFieldConfiguration,
  ] = useState<ExportFieldConfiguration>(defaultFieldConfigWithFirmographics);
  const [mergersAcquisitionsFilters, setMergersAcquisitionsFilters] =
    useState<MergersAcquisitionsFilters>(DEFAULT_MERGERS_ACQUISITIONS_FILTERS);

  // ── Dynamic step list ────────────────────────────────────────────────────
  const exportSettingsItems = useMemo<ExportSettingItem[]>(() => {
    const exportSettingsSubItems: string[] = [
      ExportCategory.Installs,
      ExportCategory.Contracts,
      ExportCategory.IntentActivity,
      ExportCategory.FunctionalAreaIntelligence,
      ...(universeEnabled
        ? [
            ExportCategory.Contacts,
            ExportCategory.ContactsFac,
            ExportCategory.BuyerIntent,
            ExportCategory.Mentions,
            ExportCategory.GenerativeAI,
            ExportCategory.FinancingEvents,
            ExportCategory.MergersAcquisitions,
          ]
        : []),
      ...(aiScoresEnabled ? [ExportCategory.Scores] : []),
    ];

    const spendSubItems: string[] = [
      ExportCategory.ITSpend,
      ExportCategory.AISpend,
      ExportCategory.ProductsAndVendors,
      ExportCategory.CloudMaturity,
      ...(universeEnabled
        ? [ExportCategory.RelativeSpend, ExportCategory.RowBasedSpend]
        : []),
    ];

    return [
      {
        main: "Export settings",
        subItems: exportSettingsSubItems,
        subItemLabels: {
          [ExportCategory.Contacts]: "Contacts [Universe Only]",
          [ExportCategory.BuyerIntent]: "Buyer Intent [Universe Only]",
          [ExportCategory.Mentions]: "Mentions [Universe Only]",
          [ExportCategory.GenerativeAI]: "Gen AI Navigator [Universe Only]",
          [ExportCategory.FinancingEvents]: "Financing Events [Universe Only]",
          [ExportCategory.MergersAcquisitions]: "M&A Events [Universe Only]",
          [ExportCategory.Scores]: "AI Scores [Universe Only]",
        } as Record<string, string>,
      },
      {
        main: "Spend Sheet",
        subItems: spendSubItems,
        subItemLabels: {
          [ExportCategory.RelativeSpend]: "Relative IT Spend [Universe Only]",
          [ExportCategory.RowBasedSpend]: "Row-Based Spend [Universe Only]",
          ...(universeEnabled
            ? { [ExportCategory.AISpend]: "AI Spend [Universe Only]" }
            : {}),
        } as Record<string, string>,
      },
      { main: "Export" },
    ];
  }, [universeEnabled, aiScoresEnabled]);

  const isAllowedCategory = useCallback(
    (_category: ExportCategory) => true,
    []
  );

  const [mainIndex, setMainIndex] = useState<number>(0);
  const [subIndex, setSubIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [estimate, setEstimate] =
    useState<EstimateCreditsDetailResponse | null>(null);
  const [isEstimatingCredits, setIsEstimatingCredits] =
    useState<boolean>(false);
  const [creditsError, setCreditsError] = useState<boolean>(false);
  const [showInsufficientError, setShowInsufficientError] = useState(false);
  const [exportErrorCode, setExportErrorCode] =
    useState<NifflerErrorCode | null>(null);
  // Last Count result and a fingerprint of the payload it priced: a quote can
  // only be saved while the settings still match what was estimated.
  const [lastEstimate, setLastEstimate] = useState<{
    estimate: ExportQuoteEstimate;
    fingerprint: string;
  } | null>(null);
  const [isQuoteDialogOpen, setIsQuoteDialogOpen] = useState(false);
  const [isUpdatingQuote, setIsUpdatingQuote] = useState(false);

  // Page-level settings (localize installs, sort) taken from a restored
  // snapshot instead of the props, so the quote exports what was priced.
  const [restoredPageSettings, setRestoredPageSettings] = useState<Partial<
    Pick<ExportSettingsSnapshot, "localizeInstalls" | "sort">
  > | null>(null);

  const resetModalUiState = useCallback(() => {
    setMainIndex(0);
    setRestoredPageSettings(null);
    setSubIndex(0);
    setSpendInclusion(EXPORT_INCLUSION_TYPES.ALL);
    setAiSpendSheetInclusion(EXPORT_INCLUSION_TYPES.NONE);
    setIsLoading(false);
    setEstimate(null);
    setIsEstimatingCredits(false);
    setCreditsError(false);
    setShowInsufficientError(false);
    setExportErrorCode(null);
    setLastEstimate(null);
    setIsQuoteDialogOpen(false);
    // Universe state
    setBuyerIntentInclusion(EXPORT_INCLUSION_TYPES.NONE);
    setMentionInclusion(EXPORT_INCLUSION_TYPES.NONE);
    setMentionIds([]);
    setMentionLookbackMonths(24);
    setMentionIncludeTimeSeries(false);
    setMentionFieldConfiguration(defaultFieldConfigWithFirmographics());
    setGenerativeAIInclusion(EXPORT_INCLUSION_TYPES.NONE);
    setGenerativeAIFieldConfiguration(defaultFieldConfigWithFirmographics());
    setRelativeSpendInclusion(EXPORT_INCLUSION_TYPES.NONE);
    setRelativeSpendIncludeSMB(false);
    setRelativeSpendFieldConfiguration(defaultFieldConfigWithFirmographics());
    setRowBasedSpendInclusion(EXPORT_INCLUSION_TYPES.NONE);
    setRowBasedSpendFieldConfiguration(defaultRowBasedSpendFieldConfig());
    setRowBasedSpendIncludeSMB(false);
    setAISpendInclusion(AI_SPEND_INCLUSION_TYPES.NONE);
    setAISpendIncludeSMB(false);
    setAISpendAbsoluteFieldConfiguration(defaultFieldConfigWithFirmographics());
    setAISpendRelativeFieldConfiguration(defaultFieldConfigWithFirmographics());
    // New universe state resets
    setInstallLookbackMonths(24);
    setInstallIncludeTimeSeries(false);
    setInstallFieldConfiguration(defaultFieldConfigWithFirmographics());
    setTimeSeriesLookbackMonths("ALLTIME");
    setTimeSeriesFieldConfiguration(defaultTimeSeriesFieldConfig());
    setIntentCollectionIds([]);
    setIntentFieldConfiguration(defaultFieldConfigWithFirmographics());
    setBuyerIntentProductIds([]);
    setContractFieldConfiguration(defaultFieldConfigWithFirmographics());
    setSpendFieldConfiguration(defaultFieldConfigWithFirmographics());
    setSpendIncludeSMB(false);
    setFaiFieldConfiguration(defaultFieldConfig());
    setFaiDepartmentIds([]);
    setFaiRoleIds([]);
    setMentionTimeSeriesFieldConfiguration(
      defaultMentionTimeSeriesFieldConfig()
    );
    // Contacts state
    setContactsInclusion(EXPORT_INCLUSION_TYPES.NONE);
    setContactsFieldConfiguration(defaultFieldConfigWithFirmographics());
    setContactsPerCompanyLimit(null);
    setContactsFac(defaultContactsFacSettings());
    setFinancingEventsInclusion(EXPORT_INCLUSION_TYPES.NONE);
    setFinancingEventsFieldConfiguration(defaultFieldConfigWithFirmographics());
    setFinancingEventsFilters(DEFAULT_FINANCING_EVENTS_FILTERS);
    setMergersAcquisitionsInclusion(EXPORT_INCLUSION_TYPES.NONE);
    setMergersAcquisitionsFieldConfiguration(
      defaultFieldConfigWithFirmographics()
    );
    setMergersAcquisitionsFilters(DEFAULT_MERGERS_ACQUISITIONS_FILTERS);
    setAIScoresInclusion(EXPORT_INCLUSION_TYPES.NONE);
    setAIScoresSourceIds([]);
    setAIScoresTiers([]);
  }, []);

  useEffect(() => {
    if (!open) return;
    resetModalUiState();
  }, [open, resetModalUiState]);

  const aiScoresSeeded = useRef(false);
  useEffect(() => {
    if (!open) {
      aiScoresSeeded.current = false;
      return;
    }
    if (aiScoresSeeded.current || !aiScoresEnabled || initialSettings) return;
    const sourceIds = [
      ...activeProfiles.map((p) => v1SourceId(p.id)),
      ...activeModels.map((m) => v2SourceId(m.family)),
    ];
    if (sourceIds.length === 0) return;
    aiScoresSeeded.current = true;
    setAIScoresInclusion(EXPORT_INCLUSION_TYPES.SELECTED);
    setAIScoresSourceIds(sourceIds);
  }, [open, aiScoresEnabled, initialSettings, activeProfiles, activeModels]);

  // ── Settings snapshot (export quotes) ────────────────────────────────────
  const snapshotSetters = useMemo<SnapshotSetters>(
    () => ({
      spendInclusion: setSpendInclusion,
      aiSpendSheetInclusion: setAiSpendSheetInclusion,
      installInclusion: setInstallInclusion,
      installProductIds: setInstallProductIds,
      installLookbackMonths: setInstallLookbackMonths,
      installIncludeTimeSeries: setInstallIncludeTimeSeries,
      installFieldConfiguration: setInstallFieldConfiguration,
      timeSeriesLookbackMonths: setTimeSeriesLookbackMonths,
      timeSeriesFieldConfiguration: setTimeSeriesFieldConfiguration,
      faiInclusion: setFaiInclusion,
      faiProductIds: setFaiProductIds,
      faiDepartmentIds: setFaiDepartmentIds,
      faiRoleIds: setFaiRoleIds,
      faiFieldConfiguration: setFaiFieldConfiguration,
      includeDecisionMakersAndInfluencersOnly:
        setIncludeDecisionMakersAndInfluencersOnly,
      contractsInclusion: setContractsInclusion,
      contractFieldConfiguration: setContractFieldConfiguration,
      intentInclusion: setIntentInclusion,
      intentTopicIds: setIntentTopicIds,
      intentTopicsCache: setIntentTopicsCache,
      intentSignalScore: setIntentSignalScore,
      intentCollectionIds: setIntentCollectionIds,
      intentFieldConfiguration: setIntentFieldConfiguration,
      buyerIntentInclusion: setBuyerIntentInclusion,
      buyerIntentProductIds: setBuyerIntentProductIds,
      mentionInclusion: setMentionInclusion,
      mentionIds: setMentionIds,
      mentionLookbackMonths: setMentionLookbackMonths,
      mentionIncludeTimeSeries: setMentionIncludeTimeSeries,
      mentionFieldConfiguration: setMentionFieldConfiguration,
      mentionTimeSeriesFieldConfiguration:
        setMentionTimeSeriesFieldConfiguration,
      generativeAIInclusion: setGenerativeAIInclusion,
      generativeAIFieldConfiguration: setGenerativeAIFieldConfiguration,
      spendFieldConfiguration: setSpendFieldConfiguration,
      spendIncludeSMB: setSpendIncludeSMB,
      relativeSpendInclusion: setRelativeSpendInclusion,
      relativeSpendIncludeSMB: setRelativeSpendIncludeSMB,
      relativeSpendFieldConfiguration: setRelativeSpendFieldConfiguration,
      rowBasedSpendInclusion: setRowBasedSpendInclusion,
      rowBasedSpendIncludeSMB: setRowBasedSpendIncludeSMB,
      rowBasedSpendFieldConfiguration: setRowBasedSpendFieldConfiguration,
      aiSpendInclusion: setAISpendInclusion,
      aiSpendIncludeSMB: setAISpendIncludeSMB,
      aiSpendAbsoluteFieldConfiguration: setAISpendAbsoluteFieldConfiguration,
      aiSpendRelativeFieldConfiguration: setAISpendRelativeFieldConfiguration,
      contactsInclusion: setContactsInclusion,
      contactsFieldConfiguration: setContactsFieldConfiguration,
      contactsPerCompanyLimit: setContactsPerCompanyLimit,
      contactsFac: setContactsFac,
      financingEventsInclusion: setFinancingEventsInclusion,
      financingEventsFieldConfiguration: setFinancingEventsFieldConfiguration,
      financingEventsFilters: setFinancingEventsFilters,
      mergersAcquisitionsInclusion: setMergersAcquisitionsInclusion,
      mergersAcquisitionsFieldConfiguration:
        setMergersAcquisitionsFieldConfiguration,
      mergersAcquisitionsFilters: setMergersAcquisitionsFilters,
      aiScoresInclusion: setAIScoresInclusion,
      aiScoresSourceIds: setAIScoresSourceIds,
      aiScoresTiers: setAIScoresTiers,
      customSpendColumns: replaceColumns,
      cloudMaturity: setIsCloudMaturityEnabled,
    }),
    [replaceColumns, setIsCloudMaturityEnabled]
  );

  const applySettingsSnapshot = useCallback(
    (
      snapshot: Partial<ExportSettingsSnapshot>,
      keys: readonly SnapshotStateKey[] = Object.keys(
        snapshotSetters
      ) as SnapshotStateKey[]
    ) => {
      keys.forEach((key) => {
        const value = snapshot[key];
        if (value === undefined) return;
        (snapshotSetters[key] as (v: typeof value) => void)(value);
      });
    },
    [snapshotSetters]
  );

  const appliedSettingsRef = useRef<Partial<ExportSettingsSnapshot> | null>(
    null
  );
  const pendingDerivedPassesRef = useRef(0);
  const wasOpenRef = useRef(false);

  // Declared after the reset so, in the same commit, the saved settings win.
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!open) {
      appliedSettingsRef.current = null;
      return;
    }
    if (!initialSettings || appliedSettingsRef.current === initialSettings) {
      return;
    }
    appliedSettingsRef.current = initialSettings;
    pendingDerivedPassesRef.current = 2;
    applySettingsSnapshot(initialSettings);
    // A saved quote opens on the final Export step; Back walks through the
    // steps with the restored settings. Saving a quote while the modal is
    // open re-applies the settings but keeps the current step.
    if (justOpened) {
      setMainIndex(exportSettingsItems.length - 1);
      setSubIndex(0);
    }
    setRestoredPageSettings({
      localizeInstalls: initialSettings.localizeInstalls,
      sort: initialSettings.sort,
    });
  }, [
    open,
    initialSettings,
    applySettingsSnapshot,
    exportSettingsItems.length,
  ]);

  useEffect(() => {
    if (pendingDerivedPassesRef.current === 0) return;
    pendingDerivedPassesRef.current -= 1;
    if (appliedSettingsRef.current) {
      applySettingsSnapshot(
        appliedSettingsRef.current,
        FILTER_DERIVED_SNAPSHOT_KEYS
      );
    }
  });

  const effectiveLocalizeInstalls =
    restoredPageSettings?.localizeInstalls ?? localizeInstalls;
  const effectiveSortEntry = restoredPageSettings
    ? restoredPageSettings.sort
    : activeSortEntry;

  const typedFilters = filters as CompanyFilters | undefined;
  const companyConfigUi = typedFilters?.companyConfigUi;

  // The contacts criteria in the shape the backend reads (OppGen's
  // buildContactFilters): the contact sheets always get them, and in "Filter
  // company list" mode the company filters do too.
  const contactFilters = useMemo(
    () => buildContactFiltersForExport(typedFilters?.contactFilters),
    [typedFilters?.contactFilters]
  );
  const contactsNarrowCompanies = contactFiltersNarrowCompanies(
    typedFilters?.contactFilters
  );

  // Export-only additions to the company filters, as OppGen sends them: the
  // uploaded file's Tier/RGIF flags on companyMatch, and the contact criteria.
  const exportFilters = useMemo(() => {
    const base = (normalizedFilters || {}) as LegacyCompanyFilter;
    const companyMatchUi = typedFilters?.companyMatchUi;
    const companyMatch =
      base.companyMatch && companyMatchUi
        ? {
            ...base.companyMatch,
            ...Object.fromEntries(
              Object.entries(companyMatchUi).filter(([, v]) => v != null)
            ),
          }
        : base.companyMatch;
    return {
      ...base,
      ...(companyMatch ? { companyMatch } : {}),
      ...(contactsNarrowCompanies && contactFilters ? { contactFilters } : {}),
    } as LegacyCompanyFilter;
  }, [
    normalizedFilters,
    typedFilters?.companyMatchUi,
    contactsNarrowCompanies,
    contactFilters,
  ]);

  const buildExportPayload = useCallback(
    (extraFilters: ExtraSearchFilters = faiExportFilterParams) => {
      const customColumns = {
        productPresence: getProductPresenceColumns(),
        vendorPresence: getVendorPresenceColumns(),
      };

      const sanitizedProfiles =
        sanitizeScoringCriteriaForBackend(activeProfiles);
      const scoringProfiles = transformFilterScoring(sanitizedProfiles);

      return getExportPayload(
        userProfile || { email: "", oktaUserId: "" },
        {
          spendInclusion,
          aiSpendInclusion: aiSpendSheetInclusion,
          installInclusion,
          contractsInclusion,
          intentInclusion,
          faiInclusion,
          installProductIds,
          faiProductIds,
          topicIds: intentTopicIds,
          intentSignalScore,
          // Matches OppGen's real export submit: Mentions/Gen AI/Relative &
          // Row-Based Spend inclusion is carried in the classic payload, but
          // specific selected Mentions can't be — there's no id slot on this
          // payload shape, so (as OppGen's CustomizerTab does) SELECTED is
          // sent as FILTERS.
          mentionInclusion:
            mentionInclusion === EXPORT_INCLUSION_TYPES.SELECTED
              ? EXPORT_INCLUSION_TYPES.FILTERS
              : mentionInclusion,
          // OppGen drops Time Series whenever installs are excluded.
          timeSeriesInclusion:
            installIncludeTimeSeries &&
            installInclusion !== EXPORT_INCLUSION_TYPES.NONE
              ? EXPORT_INCLUSION_TYPES.ALL
              : EXPORT_INCLUSION_TYPES.NONE,
          mentionTimeSeriesInclusion: mentionIncludeTimeSeries
            ? EXPORT_INCLUSION_TYPES.ALL
            : EXPORT_INCLUSION_TYPES.NONE,
          relativeSpendInclusion,
          generativeAIInclusion,
          rowBasedSpendInclusion,
        },
        effectiveLocalizeInstalls,
        exportFilters,
        customColumns,
        isCloudMaturityEnabled,
        extraFilters,
        whitespace || undefined,
        scoringProfiles,
        effectiveSortEntry,
        exportModels.map((m) => ({ family: m.family, name: m.name })),
        modelScoreFilter ?? undefined,
        getCompanyConfigSearchParams(companyConfigUi)
      );
    },
    [
      getProductPresenceColumns,
      getVendorPresenceColumns,
      faiExportFilterParams,
      userProfile,
      installInclusion,
      contractsInclusion,
      intentInclusion,
      faiInclusion,
      spendInclusion,
      aiSpendSheetInclusion,
      mentionInclusion,
      installIncludeTimeSeries,
      mentionIncludeTimeSeries,
      relativeSpendInclusion,
      generativeAIInclusion,
      rowBasedSpendInclusion,
      effectiveLocalizeInstalls,
      exportFilters,
      isCloudMaturityEnabled,
      installProductIds,
      faiProductIds,
      intentTopicIds,
      intentSignalScore,
      whitespace,
      activeProfiles,
      effectiveSortEntry,
      exportModels,
      modelScoreFilter,
      companyConfigUi,
    ]
  );

  const universeScoringProfiles = useMemo(() => {
    if (!aiScoresEnabled) return activeProfiles;
    if (aiScoresInclusion !== EXPORT_INCLUSION_TYPES.SELECTED) return [];
    return profiles.filter((p) => aiScoresSourceIds.includes(v1SourceId(p.id)));
  }, [
    aiScoresEnabled,
    aiScoresInclusion,
    aiScoresSourceIds,
    activeProfiles,
    profiles,
  ]);

  // The Universe JSON copied into covered-wagon — built by the OppGen port in
  // universePayloadBuilder.ts so both apps emit the same document.
  const buildUniversePayload = useCallback(() => {
    // OppGen always carries the selected scoring profiles in the Universe
    // JSON; covered-wagon (scoring.ex) drops the Fit/Intent score columns when
    // the list is empty. The classic payload only sends them alongside a score
    // sort, which the XLSX path needs.
    // OppGen adds the FAI refinements (departments/roles from the install
    // filters, decision-makers flag) only with enableNewFaiSheet.
    const exportPayload = buildExportPayload(
      enableNewFaiSheet ? universeFaiExportFilterParams : {}
    );
    exportPayload.params.searchParams.scoringProfiles = transformFilterScoring(
      sanitizeScoringCriteriaForBackend(universeScoringProfiles)
    );
    return buildUniverseExportPayload({
      exportPayload,
      orgId: getHgOrgId(),
      installInclusion,
      productsIds:
        installInclusion === EXPORT_INCLUSION_TYPES.SELECTED
          ? installProductIds
          : [],
      faiProductIds:
        faiInclusion === EXPORT_INCLUSION_TYPES.SELECTED ? faiProductIds : [],
      faiRoleIds:
        faiInclusion === EXPORT_INCLUSION_TYPES.SELECTED ? faiRoleIds : [],
      faiDepartmentIds:
        faiInclusion === EXPORT_INCLUSION_TYPES.SELECTED
          ? faiDepartmentIds
          : [],
      decisionMakersAndInfluencersOnly:
        enableNewFaiSheet &&
        faiInclusion !== EXPORT_INCLUSION_TYPES.NONE &&
        includeDecisionMakersAndInfluencersOnly,
      installConfiguration: installFieldConfiguration,
      timeSeriesConfiguration: timeSeriesFieldConfiguration,
      mentionInclusion,
      mentionsIds:
        mentionInclusion === EXPORT_INCLUSION_TYPES.SELECTED ? mentionIds : [],
      mentionConfiguration: mentionFieldConfiguration,
      mentionTimeSeriesConfiguration: mentionTimeSeriesFieldConfiguration,
      generativeAIInclusion,
      generativeAIConfiguration: generativeAIFieldConfiguration,
      intentInclusion,
      topicIds:
        intentInclusion === EXPORT_INCLUSION_TYPES.SELECTED
          ? intentTopicIds
          : [],
      // covered-wagon applies intentCollectionIds whatever the inclusion, so
      // a selection left over from another intent mode must not be sent.
      intentCollectionIds:
        intentInclusion === EXPORT_INCLUSION_TYPES.SELECTED_COLLECTIONS
          ? intentCollectionIds
          : [],
      filterIntentCollectionId: typedFilters?.intent?.collectionId,
      intentSignalScore,
      intentConfiguration: intentFieldConfiguration,
      contractConfiguration: contractFieldConfiguration,
      spendConfiguration: spendFieldConfiguration,
      relativeSpendConfiguration: relativeSpendFieldConfiguration,
      rowBasedSpendConfiguration: rowBasedSpendFieldConfiguration,
      faiConfiguration: faiFieldConfiguration,
      exportInstallLookback: installLookbackMonths,
      exportMentionLookback: mentionLookbackMonths,
      exportTimeSeriesLookback: timeSeriesLookbackMonths,
      exportCompaniesInstallLookback:
        getCompanyConfigInstallAge(companyConfigUi),
      includeSMBSpend: spendIncludeSMB,
      includeSMBRelativeSpend: relativeSpendIncludeSMB,
      includeSMBRowBasedSpend: rowBasedSpendIncludeSMB,
      buyerIntentInclusion,
      buyerIntentProductIds,
      contactsInclusion,
      contactsConfiguration: contactsFieldConfiguration,
      contactFilters,
      contactsPerCompanyLimit,
      contactsFac,
      financingEventsInclusion,
      financingEventsConfiguration: financingEventsFieldConfiguration,
      financingEventsFilters: pruneFinancingEventsFilters(
        financingEventsFilters
      ),
      mergersAcquisitionsInclusion,
      mergersAcquisitionsConfiguration: mergersAcquisitionsFieldConfiguration,
      mergersAcquisitionsFilters: pruneMergersAcquisitionsFilters(
        mergersAcquisitionsFilters
      ),
      aiSpendInclusion,
      absoluteAISpendConfiguration: aiSpendAbsoluteFieldConfiguration,
      relativeAISpendConfiguration: aiSpendRelativeFieldConfiguration,
      includeSMBAISpend: aiSpendIncludeSMB,
      aiScoring: aiScoresEnabled
        ? buildAIScoring(aiScoresInclusion, aiScoresSourceIds, aiScoresTiers)
        : undefined,
    });
  }, [
    buildExportPayload,
    universeScoringProfiles,
    enableNewFaiSheet,
    universeFaiExportFilterParams,
    includeDecisionMakersAndInfluencersOnly,
    installInclusion,
    installProductIds,
    faiInclusion,
    faiProductIds,
    faiRoleIds,
    faiDepartmentIds,
    installFieldConfiguration,
    timeSeriesFieldConfiguration,
    mentionInclusion,
    mentionIds,
    mentionFieldConfiguration,
    mentionTimeSeriesFieldConfiguration,
    generativeAIInclusion,
    generativeAIFieldConfiguration,
    intentInclusion,
    intentTopicIds,
    intentCollectionIds,
    typedFilters,
    intentSignalScore,
    intentFieldConfiguration,
    contractFieldConfiguration,
    spendFieldConfiguration,
    relativeSpendFieldConfiguration,
    rowBasedSpendFieldConfiguration,
    faiFieldConfiguration,
    installLookbackMonths,
    mentionLookbackMonths,
    timeSeriesLookbackMonths,
    companyConfigUi,
    spendIncludeSMB,
    relativeSpendIncludeSMB,
    rowBasedSpendIncludeSMB,
    buyerIntentInclusion,
    buyerIntentProductIds,
    contactsInclusion,
    contactsFieldConfiguration,
    contactFilters,
    contactsPerCompanyLimit,
    contactsFac,
    financingEventsInclusion,
    financingEventsFieldConfiguration,
    financingEventsFilters,
    mergersAcquisitionsInclusion,
    mergersAcquisitionsFieldConfiguration,
    mergersAcquisitionsFilters,
    aiSpendInclusion,
    aiSpendAbsoluteFieldConfiguration,
    aiSpendRelativeFieldConfiguration,
    aiSpendIncludeSMB,
    aiScoresEnabled,
    aiScoresInclusion,
    aiScoresSourceIds,
    aiScoresTiers,
  ]);

  // export-tracking-api's estimate reads the profile index from a score-sort
  // field and rejects profiles sent without one (Integer.parse(nil) ->
  // invalid_payload). Scoring doesn't change counts, so the estimate drops
  // the profiles unless the grid is sorted by a score column.
  const buildUniverseEstimatePayload = useCallback(() => {
    const payload = buildUniversePayload();
    const { searchParams } = payload.params;
    const hasScoreSort = (searchParams.sort ?? []).some(
      ({ field }) =>
        field.startsWith("Fit Score Normalized") ||
        field.startsWith("Intent Score Normalized")
    );
    return {
      ...payload,
      params: {
        ...payload.params,
        searchParams: {
          ...searchParams,
          scoringProfiles: hasScoreSort
            ? transformFilterScoring(
                sanitizeScoringCriteriaForBackend(activeProfiles)
              )
            : [],
        },
      },
    };
  }, [buildUniversePayload, activeProfiles]);

  const buildPricedPayload = universeEnabled
    ? buildUniverseEstimatePayload
    : buildExportPayload;

  const handleEstimate = useCallback(
    (result: EstimateCreditsDetailResponse, payload: unknown) => {
      setLastEstimate({
        estimate: {
          credits_estimate: result.credits_estimate,
          details: result.details,
          estimated_at: new Date().toISOString(),
          payload_kind: universeEnabled ? "universe" : "classic",
        },
        fingerprint: JSON.stringify(payload),
      });
    },
    [universeEnabled]
  );

  const currentPricedFingerprint = useMemo(
    () => (lastEstimate ? JSON.stringify(buildPricedPayload()) : null),
    [lastEstimate, buildPricedPayload]
  );
  const hasFreshEstimate =
    !!lastEstimate && currentPricedFingerprint === lastEstimate.fingerprint;

  const getSettingsSnapshot = (): ExportSettingsSnapshot => ({
    version: 1,
    spendInclusion,
    aiSpendSheetInclusion,
    installInclusion,
    installProductIds,
    installLookbackMonths,
    installIncludeTimeSeries,
    installFieldConfiguration,
    timeSeriesLookbackMonths,
    timeSeriesFieldConfiguration,
    faiInclusion,
    faiProductIds,
    faiDepartmentIds,
    faiRoleIds,
    faiFieldConfiguration,
    includeDecisionMakersAndInfluencersOnly,
    contractsInclusion,
    contractFieldConfiguration,
    intentInclusion,
    intentTopicIds,
    intentTopicsCache,
    intentSignalScore,
    intentCollectionIds,
    intentFieldConfiguration,
    buyerIntentInclusion,
    buyerIntentProductIds,
    mentionInclusion,
    mentionIds,
    mentionLookbackMonths,
    mentionIncludeTimeSeries,
    mentionFieldConfiguration,
    mentionTimeSeriesFieldConfiguration,
    generativeAIInclusion,
    generativeAIFieldConfiguration,
    spendFieldConfiguration,
    spendIncludeSMB,
    relativeSpendInclusion,
    relativeSpendIncludeSMB,
    relativeSpendFieldConfiguration,
    rowBasedSpendInclusion,
    rowBasedSpendIncludeSMB,
    rowBasedSpendFieldConfiguration,
    aiSpendInclusion,
    aiSpendIncludeSMB,
    aiSpendAbsoluteFieldConfiguration,
    aiSpendRelativeFieldConfiguration,
    contactsInclusion,
    contactsFieldConfiguration,
    contactsPerCompanyLimit,
    contactsFac,
    financingEventsInclusion,
    financingEventsFieldConfiguration,
    financingEventsFilters,
    mergersAcquisitionsInclusion,
    mergersAcquisitionsFieldConfiguration,
    mergersAcquisitionsFilters,
    aiScoresInclusion,
    aiScoresSourceIds,
    aiScoresTiers,
    customSpendColumns: customColumns,
    cloudMaturity: isCloudMaturityEnabled,
    localizeInstalls: effectiveLocalizeInstalls,
    sort: effectiveSortEntry,
  });

  const buildQuoteBody = () => ({
    search_filters: {
      ...(filters ?? {}),
      scoringSourceIds: scoringSourceIds ?? null,
    } as ExportQuoteSearchFilters,
    export_settings: getSettingsSnapshot(),
    previous_estimated: lastEstimate?.estimate ?? null,
  });

  const showSaveQuote = isSalesfacingQuotingEnabled && allowSaveQuote;
  const canUpdateQuote = showSaveQuote && !!quote?.permissions?.write;

  const handleQuoteSaved = (saved: ExportQuote) => {
    setIsQuoteDialogOpen(false);
    onQuoteSaved?.(saved);
  };

  const handleUpdateQuote = async () => {
    if (!quote) return;
    setIsUpdatingQuote(true);
    try {
      const saved = await updateExportQuote(quote.id, buildQuoteBody());
      showSuccess("Quote updated successfully");
      onQuoteSaved?.(saved);
    } catch (error) {
      console.error("Failed to update quote:", error);
      showError("Failed to update quote. Please try again.");
    } finally {
      setIsUpdatingQuote(false);
    }
  };

  const buildUniverseJSON = useCallback(
    (): string => JSON.stringify(buildUniversePayload(), null, 2),
    [buildUniversePayload]
  );

  const isOnFinalStep = mainIndex === exportSettingsItems.length - 1;
  // The final-step credits alert prices what the Export button actually sends
  // (the classic XLSX payload), as OppGen's ExportReview does. The Universe
  // payload is priced only in CreditsEstimateDialog, for data-feed quoting.
  const exportPayloadKey =
    open && isOnFinalStep && hasCreditsEnforcement
      ? JSON.stringify(buildExportPayload())
      : null;

  useEffect(() => {
    if (exportPayloadKey === null) return;

    let cancelled = false;
    setIsEstimatingCredits(true);
    setCreditsError(false);

    (async () => {
      try {
        const result = await estimateExportCredits({
          export_payload: JSON.parse(exportPayloadKey),
        });
        if (!cancelled) setEstimate(result);
      } catch (error) {
        console.error("Failed to estimate export credits:", error);
        if (!cancelled) setCreditsError(true);
      } finally {
        if (!cancelled) setIsEstimatingCredits(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [exportPayloadKey]);

  const exportData = async () => {
    setIsLoading(true);

    const data = buildExportPayload();
    try {
      await getExportResults(data);
      showSuccess(
        `The requested report is being processed and will be delivered to your inbox ${userProfile?.email} shortly.`
      );

      if (installInclusion !== EXPORT_INCLUSION_TYPES.SELECTED) {
        setInstallProductIds([]);
      }
      if (faiInclusion !== EXPORT_INCLUSION_TYPES.SELECTED) {
        setFaiProductIds([]);
        setFaiDepartmentIds([]);
        setFaiRoleIds([]);
      }
      if (intentInclusion !== EXPORT_INCLUSION_TYPES.SELECTED) {
        setIntentTopicIds([]);
        setIntentTopicsCache([]);
      }

      onClose();
    } catch (error: unknown) {
      const defaultErrorMessage = "Failed to submit export request";

      if (error instanceof NifflerExportError) {
        const errorConfig =
          NIFFLER_ERROR_MESSAGES[error.code as NifflerErrorCode];

        if (errorConfig) {
          setExportErrorCode(error.code as NifflerErrorCode);
          setShowInsufficientError(true);
        } else {
          showError(defaultErrorMessage);
        }
      } else {
        showError(defaultErrorMessage);
      }

      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async () => {
    if (mainIndex >= exportSettingsItems.length - 1) {
      exportData();
      return;
    }

    let subItemsCount = -1;

    if (Array.isArray(exportSettingsItems[mainIndex]?.subItems)) {
      subItemsCount =
        exportSettingsItems[mainIndex]?.subItems!.filter((item: string) =>
          isAllowedCategory(item as ExportCategory)
        ).length - 1;
    }

    if (subIndex < subItemsCount) {
      setSubIndex(subIndex + 1);
      return;
    }

    setMainIndex(mainIndex + 1);
    setSubIndex(0);
  };

  const handleSkipAndBack = () => {
    if (mainIndex === 0) {
      setMainIndex(1);
      setSubIndex(0);
      return;
    }

    setMainIndex(Math.max(0, mainIndex - 1));
    setSubIndex(0);
  };

  const exportCategoryComponents = useMemo(() => {
    return {
      [ExportCategory.Installs]: (
        <InstallsExportOption
          isInstallFilterEmpty={isInstallFilterEmpty}
          installInclusion={installInclusion}
          setInstallInclusion={setInstallInclusion}
          installProductIds={installProductIds}
          setInstallProductIds={setInstallProductIds}
          universeEnabled={universeEnabled}
          installLookbackMonths={installLookbackMonths}
          onInstallLookbackChange={setInstallLookbackMonths}
          installFieldConfiguration={installFieldConfiguration}
          onInstallFieldConfigChange={setInstallFieldConfiguration}
          installIncludeTimeSeries={installIncludeTimeSeries}
          onInstallIncludeTimeSeriesChange={setInstallIncludeTimeSeries}
          timeSeriesLookbackMonths={timeSeriesLookbackMonths}
          onTimeSeriesLookbackChange={setTimeSeriesLookbackMonths}
          timeSeriesFieldConfiguration={timeSeriesFieldConfiguration}
          onTimeSeriesFieldConfigChange={setTimeSeriesFieldConfiguration}
          enforceMandatoryFields={enforceMandatoryFields}
        />
      ),
      [ExportCategory.Contracts]: (
        <ContractExportOptions
          inclusion={contractsInclusion}
          setInclusion={setContractsInclusion}
          universeEnabled={universeEnabled}
          contractFieldConfiguration={contractFieldConfiguration}
          setContractFieldConfiguration={setContractFieldConfiguration}
          enforceMandatoryFields={enforceMandatoryFields}
        />
      ),
      [ExportCategory.IntentActivity]: (
        <IntentExportOptions
          isIntentFilterEmpty={isIntentFilterEmpty}
          intentInclusion={intentInclusion}
          setIntentInclusion={setIntentInclusion}
          intentTopicIds={intentTopicIds}
          setIntentTopicIds={setIntentTopicIds}
          intentTopicsCache={intentTopicsCache}
          setIntentTopicsCache={setIntentTopicsCache}
          intentSignalScore={intentSignalScore}
          setIntentSignalScore={setIntentSignalScore}
          universeEnabled={universeEnabled}
          intentCollectionIds={intentCollectionIds}
          setIntentCollectionIds={setIntentCollectionIds}
          intentFieldConfiguration={intentFieldConfiguration}
          setIntentFieldConfiguration={setIntentFieldConfiguration}
          enforceMandatoryFields={enforceMandatoryFields}
        />
      ),
      [ExportCategory.FunctionalAreaIntelligence]: (
        <FAIExportOptions
          faiDecisionMakersAndInfluencersInclusion={
            includeDecisionMakersAndInfluencersOnly
          }
          setFaiDecisionMakersAndInfluencersInclusion={
            setIncludeDecisionMakersAndInfluencersOnly
          }
          inclusion={faiInclusion}
          setInclusion={setFaiInclusion}
          isFaiFilterEmpty={isFaiFilterEmpty}
          faiProductIds={faiProductIds}
          setFaiProductIds={setFaiProductIds}
          faiDepartmentIds={faiDepartmentIds}
          faiRoleIds={faiRoleIds}
          setFaiDepartmentAndRoleIds={setFaiDepartmentAndRoleIds}
          universeEnabled={universeEnabled}
          showDecisionMakersOption={!universeEnabled || enableNewFaiSheet}
          faiFieldConfiguration={faiFieldConfiguration}
          setFaiFieldConfiguration={setFaiFieldConfiguration}
          enforceMandatoryFields={enforceMandatoryFields}
        />
      ),
      [ExportCategory.ITSpend]: (
        <ITSpendOptions
          spendInclusion={spendInclusion}
          setSpendInclusion={setSpendInclusion}
          universeEnabled={universeEnabled}
          spendFieldConfiguration={spendFieldConfiguration}
          setSpendFieldConfiguration={setSpendFieldConfiguration}
          spendIncludeSMB={spendIncludeSMB}
          setSpendIncludeSMB={setSpendIncludeSMB}
          enforceMandatoryFields={enforceMandatoryFields}
        />
      ),
      [ExportCategory.ProductsAndVendors]: (
        <ProductsAndVendorsOptions
          {...{
            customColumns,
            addCustomColumn,
            updateCustomColumn,
            deleteCustomColumn,
            toggleColumnSelection,
            replaceColumns,
            spendInclusion,
            setSpendInclusion,
            isCloudMaturityEnabled,
            setIsCloudMaturityEnabled,
          }}
        />
      ),
      [ExportCategory.CloudMaturity]: (
        <CloudMaturityOptions
          isCloudMaturityEnabled={isCloudMaturityEnabled}
          setIsCloudMaturityEnabled={setIsCloudMaturityEnabled}
        />
      ),
      // universe-json-export sections
      [ExportCategory.BuyerIntent]: (
        <BuyerIntentExportOptions
          inclusion={buyerIntentInclusion}
          setInclusion={setBuyerIntentInclusion}
          isBuyerIntentFilterEmpty={isBuyerIntentFilterEmpty}
          buyerIntentProductIds={buyerIntentProductIds}
          setBuyerIntentProductIds={setBuyerIntentProductIds}
        />
      ),
      [ExportCategory.Mentions]: (
        <MentionsExportOptions
          inclusion={mentionInclusion}
          setInclusion={setMentionInclusion}
          mentionIds={mentionIds}
          setMentionIds={setMentionIds}
          fieldConfiguration={mentionFieldConfiguration}
          setFieldConfiguration={setMentionFieldConfiguration}
          lookbackMonths={mentionLookbackMonths}
          setLookbackMonths={setMentionLookbackMonths}
          includeTimeSeries={mentionIncludeTimeSeries}
          setIncludeTimeSeries={setMentionIncludeTimeSeries}
          timeSeriesFieldConfiguration={mentionTimeSeriesFieldConfiguration}
          setTimeSeriesFieldConfiguration={
            setMentionTimeSeriesFieldConfiguration
          }
        />
      ),
      [ExportCategory.Scores]: (
        <AIScoresExportOptions
          inclusion={aiScoresInclusion}
          setInclusion={setAIScoresInclusion}
          selectedSourceIds={aiScoresSourceIds}
          setSelectedSourceIds={setAIScoresSourceIds}
          selectedTiers={aiScoresTiers}
          setSelectedTiers={setAIScoresTiers}
        />
      ),
      [ExportCategory.GenerativeAI]: (
        <GenAIExportOptions
          inclusion={generativeAIInclusion}
          setInclusion={setGenerativeAIInclusion}
          fieldConfiguration={generativeAIFieldConfiguration}
          setFieldConfiguration={setGenerativeAIFieldConfiguration}
          enforceMandatoryFields={enforceMandatoryFields}
        />
      ),
      [ExportCategory.RelativeSpend]: (
        <RelativeSpendExportOptions
          inclusion={relativeSpendInclusion}
          setInclusion={setRelativeSpendInclusion}
          fieldConfiguration={relativeSpendFieldConfiguration}
          setFieldConfiguration={setRelativeSpendFieldConfiguration}
          includeSMB={relativeSpendIncludeSMB}
          setIncludeSMB={setRelativeSpendIncludeSMB}
          enforceMandatoryFields={enforceMandatoryFields}
        />
      ),
      [ExportCategory.RowBasedSpend]: (
        <RowBasedSpendExportOptions
          inclusion={rowBasedSpendInclusion}
          setInclusion={setRowBasedSpendInclusion}
          fieldConfiguration={rowBasedSpendFieldConfiguration}
          setFieldConfiguration={setRowBasedSpendFieldConfiguration}
          includeSMB={rowBasedSpendIncludeSMB}
          setIncludeSMB={setRowBasedSpendIncludeSMB}
        />
      ),
      [ExportCategory.AISpend]: universeEnabled ? (
        <AISpendExportOptions
          inclusion={aiSpendInclusion}
          setInclusion={setAISpendInclusion}
          absoluteFieldConfiguration={aiSpendAbsoluteFieldConfiguration}
          setAbsoluteFieldConfiguration={setAISpendAbsoluteFieldConfiguration}
          relativeFieldConfiguration={aiSpendRelativeFieldConfiguration}
          setRelativeFieldConfiguration={setAISpendRelativeFieldConfiguration}
          includeSMB={aiSpendIncludeSMB}
          setIncludeSMB={setAISpendIncludeSMB}
        />
      ) : (
        <ITSpendOptions
          dataType={EXPORT_DATA_TYPES.AI_SPEND}
          spendInclusion={aiSpendSheetInclusion}
          setSpendInclusion={setAiSpendSheetInclusion}
        />
      ),
      [ExportCategory.Contacts]: (
        <ContactsExportOptions
          inclusion={contactsInclusion}
          setInclusion={handleSetContactsInclusion}
          fieldConfiguration={contactsFieldConfiguration}
          setFieldConfiguration={setContactsFieldConfiguration}
          contactsPerCompanyLimit={contactsPerCompanyLimit}
          setContactsPerCompanyLimit={setContactsPerCompanyLimit}
        />
      ),
      [ExportCategory.ContactsFac]: (
        <ContactsFacExportOptions
          settings={contactsFac}
          setSettings={handleSetContactsFac}
        />
      ),
      [ExportCategory.FinancingEvents]: (
        <FinancingEventsExportOptions
          inclusion={financingEventsInclusion}
          setInclusion={setFinancingEventsInclusion}
          filters={financingEventsFilters}
          setFilters={setFinancingEventsFilters}
          fieldConfiguration={financingEventsFieldConfiguration}
          setFieldConfiguration={setFinancingEventsFieldConfiguration}
          enforceMandatoryFields={enforceMandatoryFields}
        />
      ),
      [ExportCategory.MergersAcquisitions]: (
        <MergersAcquisitionsExportOptions
          inclusion={mergersAcquisitionsInclusion}
          setInclusion={setMergersAcquisitionsInclusion}
          filters={mergersAcquisitionsFilters}
          setFilters={setMergersAcquisitionsFilters}
          fieldConfiguration={mergersAcquisitionsFieldConfiguration}
          setFieldConfiguration={setMergersAcquisitionsFieldConfiguration}
          enforceMandatoryFields={enforceMandatoryFields}
        />
      ),
    };
  }, [
    enableNewFaiSheet,
    faiDepartmentIds,
    faiRoleIds,
    isBuyerIntentFilterEmpty,
    mentionIds,
    setFaiDepartmentAndRoleIds,
    aiScoresInclusion,
    aiScoresSourceIds,
    aiScoresTiers,
    isInstallFilterEmpty,
    installInclusion,
    contractsInclusion,
    isIntentFilterEmpty,
    intentInclusion,
    intentTopicIds,
    intentTopicsCache,
    intentSignalScore,
    includeDecisionMakersAndInfluencersOnly,
    faiInclusion,
    isFaiFilterEmpty,
    isCloudMaturityEnabled,
    setIsCloudMaturityEnabled,
    customColumns,
    addCustomColumn,
    updateCustomColumn,
    deleteCustomColumn,
    toggleColumnSelection,
    replaceColumns,
    installProductIds,
    faiProductIds,
    spendInclusion,
    aiSpendSheetInclusion,
    setInstallInclusion,
    setInstallProductIds,
    setFaiInclusion,
    setFaiProductIds,
    setIntentInclusion,
    setIntentTopicIds,
    setIntentTopicsCache,
    setIntentSignalScore,
    setSpendInclusion,
    setAiSpendSheetInclusion,
    universeEnabled,
    buyerIntentInclusion,
    mentionInclusion,
    mentionFieldConfiguration,
    mentionLookbackMonths,
    mentionIncludeTimeSeries,
    generativeAIInclusion,
    generativeAIFieldConfiguration,
    relativeSpendInclusion,
    relativeSpendIncludeSMB,
    relativeSpendFieldConfiguration,
    rowBasedSpendInclusion,
    rowBasedSpendFieldConfiguration,
    rowBasedSpendIncludeSMB,
    aiSpendInclusion,
    aiSpendIncludeSMB,
    aiSpendAbsoluteFieldConfiguration,
    aiSpendRelativeFieldConfiguration,
    contactsInclusion,
    contactsFieldConfiguration,
    contactsPerCompanyLimit,
    handleSetContactsInclusion,
    contactsFac,
    handleSetContactsFac,
    financingEventsInclusion,
    financingEventsFilters,
    financingEventsFieldConfiguration,
    mergersAcquisitionsInclusion,
    mergersAcquisitionsFilters,
    mergersAcquisitionsFieldConfiguration,
    enforceMandatoryFields,
    installLookbackMonths,
    installIncludeTimeSeries,
    installFieldConfiguration,
    timeSeriesLookbackMonths,
    timeSeriesFieldConfiguration,
    intentCollectionIds,
    intentFieldConfiguration,
    buyerIntentProductIds,
    contractFieldConfiguration,
    spendFieldConfiguration,
    spendIncludeSMB,
    faiFieldConfiguration,
    mentionTimeSeriesFieldConfiguration,
  ]);

  const exportPanelContent = useMemo(() => {
    if (mainIndex === exportSettingsItems.length - 1) {
      return (
        <Stack padding={2} data-testid="company-export-final-step">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="h5" fontWeight={500} fontSize={16}>
              Export
            </Typography>
          </Stack>
          {hasCreditsEnforcement && (
            <ExportCreditsAlert
              estimate={estimate}
              isLoading={isEstimatingCredits}
              error={creditsError}
              sx={{ mt: 1, mb: 2, mr: 3 }}
            />
          )}
          {companyCount > MAX_EXPORT_COMPANIES && (
            <Alert
              sx={{
                mt: 1,
                mb: 2,
                mr: 3,
                bgcolor: "white",
                fontSize: 12,
                border: "1px solid",
                borderColor: "warning.main",
              }}
              severity="warning"
            >
              The export will contain maximum of{" "}
              {formatNumber(MAX_EXPORT_COMPANIES)} companies
            </Alert>
          )}

          <Typography variant="subtitle1" color="textSecondary" padding={2}>
            Your report will be delivered to your inbox within a few minutes. If
            you have any questions or need additional data, please contact{" "}
            <Link href="mailto:customersupport@hginsights.com">
              customersupport@hginsights.com.
            </Link>
          </Typography>
        </Stack>
      );
    }

    const currentItem = exportSettingsItems[mainIndex];
    if (currentItem?.subItems) {
      const allowedSubItems = currentItem.subItems.filter((item) =>
        isAllowedCategory(item as ExportCategory)
      );
      const selectedSubItem = allowedSubItems[subIndex] as ExportCategory;

      if (selectedSubItem && exportCategoryComponents[selectedSubItem]) {
        return exportCategoryComponents[selectedSubItem];
      }
    }

    return null;
  }, [
    mainIndex,
    exportSettingsItems,
    hasCreditsEnforcement,
    estimate,
    isEstimatingCredits,
    creditsError,
    companyCount,
    subIndex,
    exportCategoryComponents,
    isAllowedCategory,
  ]);

  const dialogContent = useMemo(() => {
    if (isLoading) {
      return (
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          height={DIALOG_BODY_HEIGHT}
          padding={4}
        >
          <CircularProgress />
          <Typography
            variant="subtitle1"
            textAlign="center"
            color="textSecondary"
            padding={4}
          >
            Validating Export Request. You can close this window anytime and
            continue using the app. Your report will be delivered to your email
            shortly.
          </Typography>
        </Stack>
      );
    }

    return (
      <Stack
        direction="row"
        spacing={2}
        sx={{ height: DIALOG_BODY_HEIGHT, pr: 3 }}
      >
        <ExportSettings
          mainSelection={mainIndex}
          subSelection={subIndex}
          onMainSelect={(index) => {
            if (index === mainIndex) return;
            setMainIndex(index);
            setSubIndex(0);
          }}
          onSubSelect={(sindex) => setSubIndex(sindex)}
          isAllowedCategory={isAllowedCategory}
          items={exportSettingsItems}
        />
        <Box
          sx={{ flex: 1, minWidth: 0, overflowY: "auto" }}
          data-testid="export-panel-content"
        >
          {exportPanelContent}
        </Box>
      </Stack>
    );
  }, [
    exportPanelContent,
    exportSettingsItems,
    isAllowedCategory,
    isLoading,
    mainIndex,
    subIndex,
  ]);

  return (
    <>
      <CreditsEstimateDialog
        open={isCreditsDialogOpen}
        onClose={() => setIsCreditsDialogOpen(false)}
        buildExportPayload={buildPricedPayload}
        onEstimate={handleEstimate}
      />
      {showSaveQuote && (
        <ExportQuoteDialog
          open={isQuoteDialogOpen}
          mode="create"
          buildQuoteBody={buildQuoteBody}
          creditsEstimate={lastEstimate?.estimate.credits_estimate}
          onClose={() => setIsQuoteDialogOpen(false)}
          onSave={handleQuoteSaved}
        />
      )}
      {universeEnabled && (
        <UniverseJSONDialog
          open={isUniverseJSONDialogOpen}
          onClose={() => setIsUniverseJSONDialogOpen(false)}
          json={isUniverseJSONDialogOpen ? buildUniverseJSON() : ""}
        />
      )}
      <ExportErrorDialog
        open={showInsufficientError}
        onClose={() => {
          setShowInsufficientError(false);
          setExportErrorCode(null);
        }}
        zIndex={1000}
        title={
          exportErrorCode
            ? NIFFLER_ERROR_MESSAGES[exportErrorCode].title
            : "Error"
        }
        message={
          exportErrorCode
            ? NIFFLER_ERROR_MESSAGES[exportErrorCode].message
            : "An error occurred."
        }
      />
      <Dialog
        open={open}
        title={title}
        onClose={onClose}
        actions={
          isLoading ? (
            <Button
              data-testid="company-export-close-btn"
              onClick={onClose}
              color="secondary"
              variant="outlined"
            >
              Close
            </Button>
          ) : (
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              width="100%"
              padding={2}
            >
              <Button
                data-testid="company-export-cancel-btn"
                onClick={onClose}
                color="secondary"
                variant="outlined"
              >
                Cancel
              </Button>
              <Stack direction="row" spacing={2}>
                {universeEnabled && (
                  <Button
                    data-testid="company-export-view-json-btn"
                    onClick={() => setIsUniverseJSONDialogOpen(true)}
                    disabled={isLoading}
                    color="primary"
                    variant="outlined"
                  >
                    View Universe JSON
                  </Button>
                )}
                {isSalesfacingQuotingEnabled && (
                  <Button
                    data-testid="company-export-count-btn"
                    onClick={() => setIsCreditsDialogOpen(true)}
                    disabled={isLoading}
                    color="primary"
                    variant="outlined"
                  >
                    Count
                  </Button>
                )}
                {showSaveQuote && (
                  <Tooltip
                    title={
                      hasFreshEstimate
                        ? ""
                        : "Run Count on the current settings to save a quote"
                    }
                  >
                    <Stack direction="row" spacing={2}>
                      {canUpdateQuote && (
                        <Button
                          data-testid="company-export-update-quote-btn"
                          onClick={handleUpdateQuote}
                          disabled={
                            isLoading || isUpdatingQuote || !hasFreshEstimate
                          }
                          color="primary"
                          variant="outlined"
                        >
                          {isUpdatingQuote ? "Updating..." : "Update Quote"}
                        </Button>
                      )}
                      <Button
                        data-testid="company-export-save-quote-btn"
                        onClick={() => setIsQuoteDialogOpen(true)}
                        disabled={isLoading || !hasFreshEstimate}
                        color="primary"
                        variant="outlined"
                      >
                        {quote ? "Save as New Quote" : "Save Quote"}
                      </Button>
                    </Stack>
                  </Tooltip>
                )}
                <Button
                  data-testid="company-export-skip-back-btn"
                  onClick={handleSkipAndBack}
                  disabled={isLoading}
                  color="secondary"
                  variant="outlined"
                >
                  {mainIndex < exportSettingsItems.length - 1 ? "Skip" : "Back"}
                </Button>
                <Button
                  data-testid="company-export-next-export-btn"
                  onClick={handleNext}
                  disabled={isLoading}
                  color="primary"
                  variant="outlined"
                >
                  {mainIndex < exportSettingsItems.length - 1
                    ? "Next"
                    : "Export"}
                </Button>
              </Stack>
            </Stack>
          )
        }
      >
        {showSaveQuote && quote && (
          <Alert
            severity="info"
            sx={{ mx: 3, mb: 2 }}
            data-testid="company-export-quote-banner"
          >
            Quote <strong>{quote.name}</strong>
            {quote.previous_estimated
              ? ` — previous estimate: ${formatNumber(
                  quote.previous_estimated.credits_estimate
                )} credits on ${format(
                  new Date(quote.previous_estimated.estimated_at),
                  "MMM d, yyyy"
                )}.`
              : "."}{" "}
            Run Count to refresh it.
          </Alert>
        )}
        {dialogContent}
      </Dialog>
    </>
  );
};
